<template>
  <div class="file-dropzone">
    <div
      class="file-dropzone__dropzone"
      :class="dropzoneClasses"
      :data-testid="testId"
      @dragover.prevent="isDragOver = true"
      @dragenter.prevent="isDragOver = true"
      @dragleave.prevent="isDragOver = false"
      @drop.prevent="handleDrop"
      @click="openFilePicker"
    >
      <!-- The input is the keyboard control: Enter and Space open the picker natively, and the
           `:has(.file-dropzone__input:focus-visible)` rule outlines the container while it has focus. -->
      <input
        ref="fileInput"
        type="file"
        :accept="accept"
        :aria-labelledby="nameId"
        class="file-dropzone__input"
        @click.stop
        @change="handleFileChange"
      />

      <!-- `nameId` sits on whichever span is showing, so the picker is named by what is on
           screen: the instruction before a file is chosen, the file name after. -->
      <template v-if="file">
        <sp-icon name="document-csv-color" size="x-large"></sp-icon>
        <span :id="nameId" class="file-dropzone__filename">{{ file.name }}</span>
        <sp-button variant="outlined" color="primary" type="button" @click.stop="handleRemoveFile">
          {{ labels.remove }}
        </sp-button>
      </template>

      <template v-else>
        <sp-icon name="document-csv-color" size="x-large" class="file-dropzone__icon"></sp-icon>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <span :id="nameId" class="file-dropzone__text" v-html="dropzoneText" />
        <span class="file-dropzone__hint">{{ acceptHint }}</span>
      </template>

      <sp-button
        v-if="fileSpecHtml"
        variant="appearance-link"
        type="button"
        class="file-dropzone__spec-link"
        @click.stop="handleSpecLinkClick"
      >
        {{ specLinkText }}
      </sp-button>
    </div>

    <FileSpecPanel v-if="isSpecVisible" :html="fileSpecHtml" />
  </div>
</template>

<script setup>
import { computed, ref, useTemplateRef } from "vue";
import { ACCEPTED_EXTENSIONS } from "#ce/apps/SpAdmin/shared/utils/file-acceptance";
import FileSpecPanel from "./FileSpecPanel.ce.vue";

const emit = defineEmits(["file-selected", "file-removed"]);

const props = defineProps({
  /** The currently selected file, owned by the parent. */
  file: {
    type: Object,
    default: null,
  },
  /** HTML content for the dropzone instruction text; also names the file input. */
  dropzoneText: {
    type: String,
    required: true,
  },
  /** Control copy, localized by the host app: `{ remove, viewSpec, hideSpec }`. */
  labels: {
    type: Object,
    required: true,
  },
  /** Server-rendered HTML for the file specification panel. Hidden when empty. */
  fileSpecHtml: {
    type: String,
    default: "",
  },
  /** Accept attribute for the hidden file input; also drives the hint text. */
  accept: {
    type: String,
    default: ACCEPTED_EXTENSIONS.join(","),
  },
  /** Optional data-testid threaded from the specific caller. */
  testId: {
    type: String,
    default: undefined,
  },
});

const fileInput = useTemplateRef("fileInput");

// Instance-unique id for the input's `aria-labelledby`. Vue's `useId()` numbers ids per app,
// and each custom element is its own app, so it repeats across hosts on one page.
const nameId = `file-dropzone-name-${Math.random().toString(36).slice(2, 10)}`;

const isDragOver = ref(false);
const showFileSpec = ref(false);

const dropzoneClasses = computed(() => ({
  "file-dropzone__dropzone--active": isDragOver.value,
}));
const acceptHint = computed(() => props.accept.split(",").join(", "));
const specLinkText = computed(() => (showFileSpec.value ? props.labels.hideSpec : props.labels.viewSpec));
const isSpecVisible = computed(() => showFileSpec.value && !!props.fileSpecHtml);

function openFilePicker() {
  fileInput.value?.click();
}

function handleSpecLinkClick() {
  showFileSpec.value = !showFileSpec.value;
}

function emitSelectedFile(file) {
  if (!file) {
    return;
  }

  emit("file-selected", file);
}

function handleFileChange(event) {
  emitSelectedFile(event.target.files?.[0]);
  event.target.value = "";
}

function handleDrop(event) {
  isDragOver.value = false;
  emitSelectedFile(event.dataTransfer?.files?.[0]);
}

function handleRemoveFile() {
  emit("file-removed");

  if (fileInput.value) {
    fileInput.value.value = "";
  }
}
</script>

<style scoped lang="scss">
.file-dropzone {
  &__dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--sp-ref-scale-4);
    min-height: 10rem;
    padding: var(--sp-ref-scale-12) var(--sp-ref-scale-8);
    border: var(--sp-ref-scale-2) dashed var(--sp-sys-color-outline, #ccc);
    border-radius: var(--sp-ref-scale-4);
    background-color: var(--sp-sys-color-surface-variant, #f5f5f5);
    cursor: pointer;
    transition:
      border-color 0.15s,
      background-color 0.15s;

    &:hover {
      border-color: var(--sp-sys-color-primary, #1976d2);
      background-color: var(--sp-sys-color-surface, #fafafa);
    }

    // Keyed to the file input's own focus, so focusing the Remove and specification buttons does
    // not outline the whole dropzone.
    &:has(.file-dropzone__input:focus-visible) {
      outline: 2px solid var(--sp-sys-state-focus-indicator-color);
      outline-offset: var(--sp-ref-scale-2);
    }

    &--active {
      border-color: var(--sp-sys-color-primary, #1976d2);
      background-color: color-mix(in srgb, var(--sp-sys-color-primary, #1976d2) 8%, transparent);
    }
  }

  &__icon {
    opacity: 0.6;
  }

  &__text {
    font-size: var(--sp-sys-typescale-body-font-size, 1rem);
    color: var(--sp-sys-color-on-surface-variant, #666);
    text-align: center;
  }

  &__hint {
    font-size: var(--sp-sys-typescale-body-font-size, 1rem);
    color: var(--sp-sys-color-on-surface-variant, #666);
    font-style: italic;
  }

  &__filename {
    font-size: var(--sp-sys-typescale-body-font-size, 1rem);
    font-weight: var(--sp-sys-font-weight-bold, 600);
    color: var(--sp-sys-color-on-surface, #333);
    word-break: break-all;
    text-align: center;
  }

  &__spec-link {
    --sp-comp-button-appearance-link-text-color: var(--sp-sys-color-primary, #1976d2);
    --sp-comp-button-font-short: var(--sp-sys-typescale-small-font-size, 0.875rem) / 1.25 inherit;

    text-align: center;
  }

  &__input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
}
</style>
